import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/lib/api";
import { StatusBadge } from "@/components/common";

const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const sameDay = (a, b) => a.toDateString() === b.toDateString();

export function WeeklyCalendar({ items, renderAction, subtitle }) {
  const [week, setWeek] = useState(() => startOfWeek(new Date()));
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(week.getFullYear(), week.getMonth(), week.getDate() + i)), [week]);
  const shift = (n) => setWeek(new Date(week.getFullYear(), week.getMonth(), week.getDate() + n * 7));
  const end = days[6];
  const inWeek = items.filter((s) => { const d = new Date(s.start_at); return d >= week && d < new Date(end.getTime() + 86400000); }).length;
  const today = new Date();
  return (
    <div data-testid="weekly-calendar">
      <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-display text-lg font-bold text-[#0B1A33]" data-testid="calendar-week-label">
            {week.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – {end.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
          </p>
          <p className="text-xs text-slate-500" data-testid="calendar-week-count">{inWeek} live class{inWeek === 1 ? "" : "es"} this week</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => shift(-1)} data-testid="calendar-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setWeek(startOfWeek(new Date()))} data-testid="calendar-today">This week</Button>
          <Button size="sm" variant="outline" onClick={() => shift(1)} data-testid="calendar-next-week"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-7 md:divide-x md:divide-y-0">
        {days.map((d) => {
          const list = items.filter((s) => sameDay(new Date(s.start_at), d)).sort((a, b) => a.start_at.localeCompare(b.start_at));
          const isToday = sameDay(d, today);
          return (
            <div key={d.toISOString()} className={`min-h-0 p-3 md:min-h-[320px] ${isToday ? "bg-[#1F6FFF]/[0.04]" : ""}`} data-testid={`calendar-day-${d.toISOString().slice(0, 10)}`}>
              <div className="mb-3 flex items-baseline gap-2 md:flex-col md:gap-0">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
                <span className={`font-display text-xl font-extrabold ${isToday ? "text-[#1F6FFF]" : "text-[#0B1A33]"}`}>{d.getDate()}</span>
              </div>
              <div className="space-y-2">
                {list.map((s) => (
                  <div key={s.id} className={`rise rounded-xl border-l-4 bg-white p-2.5 shadow-sm ring-1 ring-slate-200/70 ${s.status === "in_progress" ? "border-cyan-500" : "border-[#3CC34A]"}`} data-testid={`calendar-event-${s.id}`}>
                    <p className="text-xs font-bold text-[#1F6FFF]">{fmtTime(s.start_at)} · {s.duration}m</p>
                    <p className="mt-0.5 line-clamp-2 text-sm font-semibold leading-snug text-[#0B1A33]">{s.title}</p>
                    <p className="truncate text-xs text-slate-500">{subtitle(s)}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <StatusBadge status={s.status} />
                      {renderAction?.(s)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
